/* eslint-disable @typescript-eslint/no-explicit-any */
import { memoize } from "lodash";
import mudder from "mudder";

const CHARS = " !-.0123456789:ABCDEFGHIJKLMNOPQRSTUVWXYZ_abcdefghijklmnopqrstuvwxyz~";

// See between.py

for (let i = 1; i < CHARS.length; i += 1) {
	console.assert(CHARS[i - 1] < CHARS[i]);
}

// takes about 3ms to initialize on a very fast computer.
export const getSymbolTable = memoize(() => new mudder.SymbolTable(CHARS));

export const numStrings = 100;

export enum Bias {
	FIRST,
	MIDDLE,
	LAST,
}

export const getNextIndex = (currentIndex: number, stringBias: Bias) => {
	if (stringBias === Bias.LAST) {
		return currentIndex - 1;
	} else if (stringBias === Bias.MIDDLE) {
		return -currentIndex - Number(currentIndex >= 0);
	}
	return currentIndex + 1;
};

export const nowUtcMySqlFormat = () => {
	const now = new Date();
	// Match how MySQL formats the assignee_changed_at datetime(6):
	// https://dev.mysql.com/doc/refman/8.0/en/datetime.html
	// Months starts at 0 in Javascript so we need to add 1 to it
	return `${now.getUTCFullYear()}-${(now.getUTCMonth() + 1).toString().padStart(2, "0")}-${now
		.getUTCDate()
		.toString()
		.padStart(2, "0")} ${now.getUTCHours().toString().padStart(2, "0")}:${now
		.getUTCMinutes()
		.toString()
		.padStart(2, "0")}:${now.getUTCSeconds().toString().padStart(2, "0")}.${now
		.getUTCMilliseconds()
		.toString()
		.padEnd(6, "0")}`;
};

type BetweenOptions = {
	num?: number;
	chars?: string;
	allowEqual?: boolean;
	maxLength?: number;
	blackList?: Set<string>;
	stringBias?: Bias;
};

type Between = {
	(a: string, b: string, options?: BetweenOptions & { num: undefined }): string;
	(a: string, b: string, options?: BetweenOptions & { num: number }): string[];
};

const between: Between = (
	a: string,
	b: string,
	{ num = undefined, allowEqual = false, maxLength = 96, blackList, stringBias = Bias.MIDDLE }: BetweenOptions = {},
): any => {
	if (a > b) {
		throw new Error(`${a} should be less than ${b}`);
	}
	if (a === b) {
		if (allowEqual) {
			return a;
		}
		throw new Error(`${a} should be less than ${b}`);
	}

	const results = getSymbolTable().mudder(a, b, num ?? numStrings);

	if (!num) {
		let stringIndex =
			stringBias === Bias.LAST ? numStrings - 1 : stringBias === Bias.MIDDLE ? Math.ceil(numStrings / 2) : 0;
		let stringAtPosition = results[stringIndex];

		if (blackList || stringAtPosition.length > maxLength) {
			while ((blackList && blackList.has(stringAtPosition)) || stringAtPosition.length > maxLength) {
				stringIndex = getNextIndex(stringIndex, stringBias);
				stringAtPosition = results[stringIndex >= 0 ? stringIndex : numStrings + stringIndex];

				if (!stringAtPosition) {
					throw new Error(`All results are either in black list or longer than max_length=${maxLength}`);
				}
			}
		}
		return stringAtPosition;
	} else {
		results.forEach((res, i) => {
			let result = res;
			if (blackList && blackList.has(result)) {
				result = results[i] = between(i === 0 ? a : results[i - 1], result, {
					maxLength,
					blackList,
					num: undefined,
				});
			}
			if (result.length > maxLength) {
				throw new Error(`Cannot get string between ${a} and ${b} given maxLength=${maxLength}`);
			}
		});
		if (num === undefined) return results[0];
		return results;
	}
};

export default between;
